/**
 * The shell: who you are, where you are, and the footer that says which
 * environment answered.
 *
 * Everything below it is one of the screens in `src/screens`. `src/routes.ts`
 * decides which from the URL — a path, not component state, so a group's
 * screen survives a refresh and can be sent to somebody — and the sign-in
 * screen stands in front of all of them, because every `/api` route resolves
 * the session cookie first and there is nothing for a stranger to render.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  describeSignInError,
  fetchSession,
  readSignInError,
  signOut,
  signOutEverywhere,
  type SessionState,
} from './auth'
import { QueueBanner } from './components/QueueBanner'
import { SettingsPanel } from './components/SettingsPanel'
import { fetchHealth, type Health } from './health'
import { Admin } from './screens/Admin'
import { Group } from './screens/Group'
import { GroupActivity } from './screens/GroupActivity'
import { Home } from './screens/Home'
import { Invite } from './screens/Invite'
import { Project as ProjectScreen } from './screens/Project'
import { ReceiptLibrary } from './screens/ReceiptLibrary'
import { Recurring } from './screens/Recurring'
import { SignIn } from './screens/SignIn'
import { showMotion } from './motion-document'
import { screenKey, useRoute } from './routes'

export default function App() {
  const [health, setHealth] = useState<Health | null>(null)
  const [session, setSession] = useState<SessionState | null>(null)
  const [busy, setBusy] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // The API redirects failures back here as ?signin_error=<code>.
  const [signInError] = useState(() => readSignInError(window.location.search))
  const { route, navigate } = useRoute()

  useEffect(() => {
    let cancelled = false
    fetchHealth().then((result) => {
      if (!cancelled) setHealth(result)
    })
    fetchSession().then((result) => {
      if (!cancelled) setSession(result)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const leave = useCallback(async (everywhere: boolean) => {
    setBusy(true)
    await (everywhere ? signOutEverywhere() : signOut())
    setSession(await fetchSession())
    setSettingsOpen(false)
    setBusy(false)
  }, [])

  const user = session?.status === 'ok' ? session.user : null

  /**
   * The account's answer, arriving after the inline script's guess. When they
   * agree — which is every load after the first — this changes nothing; when
   * they do not, it is one attribute on `<html>` and every animation in the
   * app is already reading it. See `src/motion.ts` for why the guess exists
   * at all: a list that has already staggered in cannot be un-staggered by a
   * preference that turns up afterwards.
   */
  useEffect(() => {
    if (user) showMotion(user.motion)
  }, [user?.motion])

  return (
    <main className="app">
      <header className="masthead">
        <div>
          <h1>
            <button type="button" className="wordmark" onClick={() => navigate({ name: 'home' })}>
              FriendLoan
            </button>
          </h1>
          <p className="tagline">Lending between friends, tracked properly.</p>
        </div>
        {user && (
          <div className="whoami">
            <p className="whoami__name">{user.displayName}</p>
            {/*
              One button, and everything that used to be spelled out here is
              behind it: the theme picker, the motion toggle, the admin link
              and both ways of signing out. They were in the masthead because
              it is the one thing on every screen — which is still true of the
              button, and a settings sheet is reachable from anywhere without
              five controls of permanent chrome above the first card.
              `src/components/SettingsPanel.tsx` is what opens.
            */}
            <p className="whoami__actions">
              <button
                type="button"
                className="button--quiet"
                aria-haspopup="dialog"
                aria-expanded={settingsOpen}
                onClick={() => setSettingsOpen(true)}
              >
                Settings
              </button>
            </p>
          </div>
        )}
      </header>

      {/*
        The sheet, drawn outside the masthead although the button that opens it
        is inside: it is fixed to the screen rather than to the band, and a
        `<div>` inside the `<p>` up there would not be markup at all.

        Signing out does not close it — it stays up with both its buttons
        disabled while the request is in flight, and `leave` closes it once
        there is nobody left to show it to.
      */}
      {user && settingsOpen && (
        <SettingsPanel
          motion={user.motion}
          isAdmin={user.isAdmin}
          busy={busy}
          onAdmin={() => {
            navigate({ name: 'admin' })
            setSettingsOpen(false)
          }}
          onSignOut={(everywhere) => void leave(everywhere)}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {/*
        Above every screen rather than inside one: a write can be queued for a
        group nobody is looking at, and "something is waiting" has to be true
        wherever the app happens to be open. It draws nothing on its own —
        `QueueBanner` decides that — so a signed-out visitor and a stranger on
        an invite link see nothing different here.
      */}
      {user && <QueueBanner />}

      {/*
        One box around the screens, keyed by which screen it is, so that
        changing screen is a cross-fade rather than a cut — and an opacity
        cross-fade only. Nothing here translates, so nothing under the pointer
        moves while it happens and the fade survives reduced motion untouched:
        a cross-fade is the animation reduced motion *keeps*.

        The key is `screenKey` and not the route, which is what stops opening a
        receipt — a route of its own, so the back gesture closes it — from
        tearing down the grid it is a picture over.
      */}
      <div className="screen" key={screenKey(route)}>
        {session === null && <p className="muted">Checking who you are…</p>}
        {session?.status === 'error' && <p className="bad">{describeSignInError(session.error)}</p>}
        {/*
          An invite link is the one screen a stranger may be on, so it renders
          instead of the sign-in card rather than behind it — somebody who has
          just been sent a link has to be able to see what it is before being
          asked to sign in to it. The screen puts the sign-in card underneath
          itself when there is nobody signed in, and `signInUrl` carries this
          path as `next`, so signing in comes back here.
        */}
        {session !== null && route.name === 'invite' && <Invite token={route.token} />}
        {session !== null && !user && route.name !== 'invite' && (
          <SignIn signInError={signInError} />
        )}

        {user && route.name === 'home' && <Home myId={user.id} navigate={navigate} />}
        {user && route.name === 'group' && (
          <Group groupId={route.groupId} myId={user.id} navigate={navigate} />
        )}
        {user && route.name === 'activity' && (
          <GroupActivity groupId={route.groupId} navigate={navigate} />
        )}
        {user && route.name === 'project' && (
          <ProjectScreen
            groupId={route.groupId}
            projectId={route.projectId}
            myId={user.id}
            navigate={navigate}
          />
        )}
        {user && route.name === 'recurring' && (
          <Recurring groupId={route.groupId} myId={user.id} navigate={navigate} />
        )}
        {/*
          The grid and the viewer are one screen with two URLs: the viewer is a
          full-bleed overlay over the grid it came from, and giving it a path of
          its own is what makes the phone's back gesture close it instead of
          leaving the group. `src/lib/route.ts` says the same thing from the
          other end.
        */}
        {user && route.name === 'receipts' && (
          <ReceiptLibrary
            groupId={route.groupId}
            myId={user.id}
            openReceiptId={null}
            navigate={navigate}
          />
        )}
        {user && route.name === 'receipt' && (
          <ReceiptLibrary
            groupId={route.groupId}
            myId={user.id}
            openReceiptId={route.receiptId}
            navigate={navigate}
          />
        )}
        {user && route.name === 'admin' && <Admin isAdmin={user.isAdmin} />}
        {user && route.name === 'unknown' && (
          <section className="card">
            <h2>Nothing here</h2>
            <p className="muted">
              There is no screen at <code>{route.path}</code>.
            </p>
            <p className="actions">
              <button type="button" onClick={() => navigate({ name: 'home' })}>
                Back to your home screen
              </button>
            </p>
          </section>
        )}

      </div>

      <footer className="footer">
        {health?.status === 'ok' && (
          <p className="muted">
            {health.environment} · schema {health.db.schemaVersion} · {health.db.latencyMs} ms
          </p>
        )}
        {health?.status === 'error' && <p className="bad">{health.error}</p>}
      </footer>
    </main>
  )
}
